import type { GameConfig } from "../config/gameConfig";
import { stableHash } from "./gameMath";
import type { CurrencyAmount } from "./types";

export type RelicDuelImpulse = 1 | 2 | 3 | 4 | 5;
export type RelicDuelRoundWinner = "player" | "opponent" | "tie";
export type RelicDuelResult = "win" | "loss" | "draw";
export type RelicDuelPhase = "choosing" | "reveal" | "result";

export type RelicDuelRound = {
  readonly index: number;
  readonly relicValue: RelicDuelImpulse;
  readonly pot: number;
  readonly playerImpulse: RelicDuelImpulse;
  readonly opponentImpulse: RelicDuelImpulse;
  readonly playerAuto: boolean;
  readonly winner: RelicDuelRoundWinner;
  readonly awardedPoints: number;
};

export type RelicDuelMatch = {
  readonly seed: string;
  readonly stake: CurrencyAmount;
  readonly phase: RelicDuelPhase;
  readonly roundIndex: number;
  readonly relicOrder: readonly RelicDuelImpulse[];
  readonly opponentPlan: readonly RelicDuelImpulse[];
  readonly playerRemaining: readonly RelicDuelImpulse[];
  readonly opponentRemaining: readonly RelicDuelImpulse[];
  readonly carryPoints: number;
  readonly playerScore: number;
  readonly opponentScore: number;
  readonly history: readonly RelicDuelRound[];
  readonly result: RelicDuelResult | null;
};

export type RelicDuelSetup = {
  readonly relicOrder?: readonly RelicDuelImpulse[];
  readonly opponentPlan?: readonly RelicDuelImpulse[];
};

function assertPermutation(values: readonly RelicDuelImpulse[], expected: readonly RelicDuelImpulse[], label: string): void {
  if (
    values.length !== expected.length ||
    [...values].sort((left, right) => left - right).some((value, index) => value !== expected[index])
  ) {
    throw new Error(`${label} must contain every duel value exactly once`);
  }
}

function deterministicPermutation(
  source: readonly RelicDuelImpulse[],
  seed: string,
  salt: string,
  numericSeed: number,
): RelicDuelImpulse[] {
  const values = [...source];
  for (let index = values.length - 1; index > 0; index -= 1) {
    const swapIndex = stableHash(`${seed}:${salt}:${index}`, numericSeed) % (index + 1);
    const current = values[index];
    const replacement = values[swapIndex];
    if (current === undefined || replacement === undefined) throw new Error("Duel permutation is incomplete");
    values[index] = replacement;
    values[swapIndex] = current;
  }
  return values;
}

function withoutImpulse(values: readonly RelicDuelImpulse[], impulse: RelicDuelImpulse): RelicDuelImpulse[] {
  const index = values.indexOf(impulse);
  if (index < 0) throw new Error(`Impulse ${impulse} has already been used`);
  return values.filter((_, candidateIndex) => candidateIndex !== index);
}

function resultForScores(playerScore: number, opponentScore: number): RelicDuelResult {
  if (playerScore === opponentScore) return "draw";
  return playerScore > opponentScore ? "win" : "loss";
}

export function createRelicDuelMatch(
  seed: string,
  stake: CurrencyAmount,
  config: GameConfig,
  setup: RelicDuelSetup = {},
): RelicDuelMatch {
  const impulses = config.relicDuel.impulseValues;
  const relics = config.relicDuel.relicValues;
  const relicOrder = setup.relicOrder === undefined
    ? deterministicPermutation(relics, seed, "relics", config.relicDuel.deterministicSeed)
    : [...setup.relicOrder];
  const opponentPlan = setup.opponentPlan === undefined
    ? deterministicPermutation(impulses, seed, "guardian", config.relicDuel.deterministicSeed)
    : [...setup.opponentPlan];
  assertPermutation(relicOrder, relics, "Relic order");
  assertPermutation(opponentPlan, impulses, "Opponent plan");
  return {
    seed,
    stake,
    phase: "choosing",
    roundIndex: 0,
    relicOrder,
    opponentPlan,
    playerRemaining: [...impulses],
    opponentRemaining: [...impulses],
    carryPoints: 0,
    playerScore: 0,
    opponentScore: 0,
    history: [],
    result: null,
  };
}

export function currentRelicDuelPot(match: RelicDuelMatch): number {
  if (match.phase === "result") return match.carryPoints;
  return match.carryPoints + (match.relicOrder[match.roundIndex] ?? 0);
}

export function autoSelectRelicDuelImpulse(match: RelicDuelMatch, config: GameConfig): RelicDuelImpulse {
  if (match.phase !== "choosing" || match.playerRemaining.length === 0) {
    throw new Error("Relic Duel is not accepting an automatic move");
  }
  const index = stableHash(
    `${match.seed}:player-timeout:${match.roundIndex}`,
    config.relicDuel.deterministicSeed,
  ) % match.playerRemaining.length;
  const impulse = match.playerRemaining[index];
  if (impulse === undefined) throw new Error("Relic Duel has no automatic move");
  return impulse;
}

export function resolveRelicDuelRound(
  match: RelicDuelMatch,
  playerImpulse: RelicDuelImpulse,
  playerAuto = false,
): RelicDuelMatch {
  if (match.phase !== "choosing") throw new Error("Relic Duel round is not accepting a move");
  if (!match.playerRemaining.includes(playerImpulse)) throw new Error(`Impulse ${playerImpulse} is unavailable`);
  const relicValue = match.relicOrder[match.roundIndex];
  const opponentImpulse = match.opponentPlan[match.roundIndex];
  if (relicValue === undefined || opponentImpulse === undefined) throw new Error("Relic Duel round is missing");
  if (!match.opponentRemaining.includes(opponentImpulse)) throw new Error("Opponent plan reused an impulse");

  const pot = match.carryPoints + relicValue;
  const winner: RelicDuelRoundWinner = playerImpulse === opponentImpulse
    ? "tie"
    : playerImpulse > opponentImpulse
      ? "player"
      : "opponent";
  const awardedPoints = winner === "tie" ? 0 : pot;
  const round: RelicDuelRound = {
    index: match.roundIndex,
    relicValue,
    pot,
    playerImpulse,
    opponentImpulse,
    playerAuto,
    winner,
    awardedPoints,
  };

  return {
    ...match,
    phase: "reveal",
    playerRemaining: withoutImpulse(match.playerRemaining, playerImpulse),
    opponentRemaining: withoutImpulse(match.opponentRemaining, opponentImpulse),
    carryPoints: winner === "tie" ? pot : 0,
    playerScore: match.playerScore + (winner === "player" ? pot : 0),
    opponentScore: match.opponentScore + (winner === "opponent" ? pot : 0),
    history: [...match.history, round],
  };
}

export function advanceRelicDuelMatch(match: RelicDuelMatch): RelicDuelMatch {
  if (match.phase !== "reveal") throw new Error("Relic Duel reveal is not ready to advance");
  const finalRound = match.roundIndex >= match.relicOrder.length - 1;
  if (finalRound) {
    return {
      ...match,
      phase: "result",
      result: resultForScores(match.playerScore, match.opponentScore),
    };
  }
  return {
    ...match,
    phase: "choosing",
    roundIndex: match.roundIndex + 1,
  };
}
